import type { IApplicantInsightsProps } from "../types";
import type { View } from "react-native";

import { getTodayCountText } from "./ApplicantInsightsCount";
import ApplicantInsightsHeader from "./ApplicantInsightsHeader";
import { ApplicantInsightsSkillsHeader } from "./ApplicantInsightsSkillsHeader";
import DemographicChart from "./DemographicChart";
import { Divider, Box, Button, Flex, Text, Alert } from "../../../../../../mads";
import { Discover } from "../../../../../../mads/icons";
import { useI18n } from "../../../../../../shared/I18n";
import { handleDisplayOnboarding } from "../utils/onboardingUtils";

import { colors } from "@indeed/ifl-tokens/react-native";
import React, { useRef } from "react";

const BlurredDemographicChart = (): React.JSX.Element => {
  const gt = useI18n();

  const segmentStyles = [
    { backgroundColor: colors.neutral500, width: "50%" },
    { backgroundColor: colors.background, width: "50%" },
  ] as const;
  return (
    <Box sx={{ marginBlockEnd: 32 }}>
      <Flex
        role="img"
        aria-label={gt.gettext("Percent stack chart")}
        sx={{
          flexDirection: "row",
          height: 7,
          width: "100%",
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: colors.borderWeak,
          borderRadius: 5,
          overflow: "hidden",
        }}
      >
        {segmentStyles.map((style, index) => (
          <Box key={index} sx={style} />
        ))}
      </Flex>
    </Box>
  );
};

export const BlurredApplicantInsightsPreviewCTA = (props: IApplicantInsightsProps) => {
  const {
    count,
    previewInsight,
    country,
    parentLogId,
    hostId,
    pageId,
    accountId,
    providerId,
    reload,
  } = props;
  const gt = useI18n();

  const buttonRef = useRef<View | null>(null);
  const isTotalApplicantUnknown = count.totalCount === -1;
  const isEarlyApplicant: boolean = !isTotalApplicantUnknown && count.totalCount < 5;
  const handleOnboardingClick = async (): Promise<void> => {
    await handleDisplayOnboarding({
      country,
      parentLogId,
      hostId,
      pageId,
      from: "vjp_cta_applicant_insight_preview",
      accountId,
      providerId,
      reload,
    });
  };

  const titleText = getTodayCountText(gt, count);
  const detailText = gt.gettext("Unlock Pro to see total number of applicants.");

  return (
    <Flex
      sx={{
        flexDirection: "column",
        marginInlineStart: -14,
        marginInlineEnd: -14,
      }}
    >
      <Box sx={{ paddingInlineStart: 14, paddingInlineEnd: 14 }}>
        <ApplicantInsightsHeader showPopover={false} showBranding={true} />
        <Box sx={{ marginBlockEnd: 0 }} data-testid="applicant-insights-count">
          <Alert
            // The 'title' should only be bold if isEarlyApplicant is false.
            // If this is an early applicant, the 'title' prepended to the 'explainerText' so everything has a consistent font weight.
            title={isEarlyApplicant ? undefined : titleText}
            explainerText={isEarlyApplicant ? titleText + "\n" + detailText : detailText}
            status={"info"}
            icon={<Discover color={colors.foregroundStatusInfo} />}
          />
        </Box>
        <ApplicantInsightsSkillsHeader />
        <Box
          sx={{
            borderRadius: 12,
            borderWidth: 1,
            borderColor: colors.borderWeak,
            borderStyle: "solid",
            paddingInline: 12,
            paddingBlockStart: 12,
          }}
          data-testid="applicant-insights-demographics"
        >
          <DemographicChart key={previewInsight!.title} item={previewInsight!} />
          <BlurredDemographicChart />
          <BlurredDemographicChart />
          <Flex>
            <Text
              level={2}
              sx={{
                color: colors.foregroundWeak,
                fontStyle: "italic",
              }}
            >
              {" "}
              {gt.gettext("3 other insights available")}
            </Text>
          </Flex>
          <Box sx={{ marginTop: 16, marginBottom: 16 }}>
            <Button
              variant="secondary"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises -- FIXME
              onPress={handleOnboardingClick}
              ref={buttonRef}
            >
              {gt.gettext("Unlock all insights with Pro")}
            </Button>
          </Box>
        </Box>
      </Box>
      <Divider
        sx={{
          marginTop: 8,
          marginBottom: 8,
        }}
      />
    </Flex>
  );
};
